import type { ScanCallableEntity } from '@/components/smartai/api/getCallGraph';
import type { TestOptimizationMethodDto } from '@/components/smartai/api/compareBuildDiff';
import { formatCsv } from '@/utils/downloadJson';

export type Severity =
  | 'CRITICAL'
  | 'IMPORTANT'
  | 'LOW'
  | 'COVERED'
  | 'ORPHAN'
  | 'LEAF';

export type MethodRow = {
  entityKey: string;
  name: string;
  type: string;
  filePath: string;
  lineStart: number;
  lineEnd: number;
  severity: Severity;
  score: number;
  fanIn: number;
  fanOut: number;
  coveredBy: string[];
  entity: ScanCallableEntity;
  /** ADO diff status: added, modified, or deleted (build-to scan). */
  changeType?: 'added' | 'modified' | 'deleted';
  /** False when build-diff could not map the row to scanEntities (not saveable). */
  matchedInScanEntities?: boolean;
};

export function isMethodRowSavable(row: MethodRow): boolean {
  return row.matchedInScanEntities !== false;
}

function toSeverity(raw: string): Severity {
  const u = raw.toUpperCase();
  if (
    u === 'CRITICAL' ||
    u === 'IMPORTANT' ||
    u === 'LOW' ||
    u === 'COVERED' ||
    u === 'ORPHAN' ||
    u === 'LEAF'
  ) {
    return u as Severity;
  }
  return 'LOW';
}

/** Map backend ``testOptimizationMethods`` (scanEntities + entityTestHits) to table rows. */
export function mapTestOptimizationMethodsToRows(
  methods: TestOptimizationMethodDto[],
): MethodRow[] {
  return methods.map(m => {
    const ent = (m.entity ?? {}) as ScanCallableEntity;
    const lineStart = m.lineStart ?? 0;
    const diffPart =
      m.diffIndex !== undefined
        ? `diff:${m.diffIndex}:${m.matchIndex ?? 0}:`
        : '';
    const uniqueKey =
      diffPart +
      (m.entityId?.trim() ||
        (lineStart > 0
          ? `${m.entityKey}:L${lineStart}`
          : m.entityKey));
    return {
      entityKey: uniqueKey,
      name: m.entityName,
      type: m.entityType,
      filePath: m.filePath,
      lineStart,
      lineEnd: m.lineEnd ?? 0,
      severity: toSeverity(m.severity),
      score: m.score ?? 0,
      fanIn: m.fanIn ?? 0,
      fanOut: m.fanOut ?? 0,
      coveredBy: m.coveredBy ?? [],
      entity: {
        ...ent,
        entityKey: m.entityKey,
        name: m.entityName,
        type: m.entityType,
        filePath: m.filePath,
        lineStart: m.lineStart,
        lineEnd: m.lineEnd,
        entityId: m.entityId,
      },
      changeType: m.changeType,
      matchedInScanEntities: m.matchedInScanEntities !== false,
    };
  });
}

/**
* Identifier for API calls (test run spec, etc.) — not the UI table row key.
* Build-diff rows prefix ``diff:{index}:{match}:`` for React keys only.
*/
export function resolveScanEntityIdentifier(row: MethodRow): string {
  const ent = row.entity ?? {};
  const entityId =
    typeof ent.entityId === 'string' ? ent.entityId.trim() : '';
  if (entityId) return entityId;

  const scanEntityKey =
    typeof ent.entityKey === 'string' ? ent.entityKey.trim() : '';
  if (scanEntityKey && !scanEntityKey.startsWith('diff:')) {
    return scanEntityKey;
  }

  const rowKey = (row.entityKey ?? '').trim();
  const stripped = rowKey.replace(/^diff:\d+:\d+:/, '');
  return stripped || scanEntityKey || rowKey;
}

export type FilterKey =
  | 'all'
  | 'added'
  | 'modified'
  | 'deleted'
  | 'critical'
  | 'important'
  | 'low'
  | 'covered'
  | 'orphans'
  | 'leaves';

export function fanInKeys(ent: ScanCallableEntity): string[] {
  const raw = ent.metadata?.fanIn;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  return [];
}

export function fanOutCount(ent: ScanCallableEntity): number {
  const m = ent.metadata;
  if (!m) return 0;
  if (typeof m.fanOut === 'number') return m.fanOut;
  const resolved = m.resolvedFanout ?? m.resolvedFanouts ?? m.rawFanout ?? [];
  return Array.isArray(resolved) ? resolved.length : 0;
}

export function deriveSeverity(ent: ScanCallableEntity): Severity {
  const fi = fanInKeys(ent).length;
  const fo = fanOutCount(ent);
  const total = ent.metadata?.scores?.total ?? 0;
  const orphanScore = ent.metadata?.scores?.orphan ?? 0;
  const leafScore = ent.metadata?.scores?.leaf ?? 0;

  const coveredBy = (ent as ScanCallableEntity & { coveredBy?: string[] })
    .coveredBy;
  if (coveredBy && coveredBy.length > 0) return 'COVERED';

  if (orphanScore < 0 || (fi === 0 && fo === 0)) return 'ORPHAN';
  if (leafScore < 0 || fo === 0) return 'LEAF';
  if (total >= 80) return 'CRITICAL';
  if (total >= 40) return 'IMPORTANT';
  return 'LOW';
}

export function flattenCallables(
  entitiesByFile: Record<string, ScanCallableEntity[]>,
): MethodRow[] {
  const rows: MethodRow[] = [];
  for (const [filePath, list] of Object.entries(entitiesByFile)) {
    for (const ent of list) {
      const entityKey = ent.entityKey ?? `${filePath}:${ent.name}:${ent.lineStart}`;
      rows.push({
        entityKey,
        name: ent.name ?? 'unknown',
        type: ent.type ?? 'Method',
        filePath: ent.filePath ?? filePath,
        lineStart: ent.lineStart ?? 0,
        lineEnd: ent.lineEnd ?? 0,
        severity: deriveSeverity(ent),
        score: ent.metadata?.scores?.total ?? 0,
        fanIn: fanInKeys(ent).length,
        fanOut: fanOutCount(ent),
        coveredBy: [],
        entity: ent,
      });
    }
  }
  return rows.sort((a, b) => b.score - a.score);
}

export function filterMethods(
  rows: MethodRow[],
  filter: FilterKey,
  search: string,
): MethodRow[] {
  let out = rows;
  switch (filter) {
    case 'added':
      out = out.filter(r => r.changeType === 'added');
      break;
    case 'modified':
      out = out.filter(r => r.changeType === 'modified');
      break;
    case 'deleted':
      out = out.filter(r => r.changeType === 'deleted');
      break;
    case 'critical':
      out = out.filter(r => r.severity === 'CRITICAL');
      break;
    case 'important':
      out = out.filter(r => r.severity === 'IMPORTANT');
      break;
    case 'low':
      out = out.filter(r => r.severity === 'LOW');
      break;
    case 'covered':
      out = out.filter(r => r.severity === 'COVERED' || r.coveredBy.length > 0);
      break;
    case 'orphans':
      out = out.filter(r => r.severity === 'ORPHAN');
      break;
    case 'leaves':
      out = out.filter(r => r.severity === 'LEAF');
      break;
    default:
      break;
  }
  const q = search.trim().toLowerCase();
  if (!q) return out;
  return out.filter(
    r =>
      r.name.toLowerCase().includes(q) ||
      r.filePath.toLowerCase().includes(q) ||
      r.entityKey.toLowerCase().includes(q),
  );
}

/** Change-type chip counts for Build Diff (align with Compare ``entities`` totals). */
export function countByFilterWithDiffSummary(
  rows: MethodRow[],
  summary?: {
    diffEntityCount: number;
    diffByChangeType: { added: number; modified: number; deleted: number };
  },
): Record<FilterKey, number> {
  const base = countByFilter(rows);
  if (!summary) return base;
  return {
    ...base,
    all: summary.diffEntityCount,
    added: summary.diffByChangeType.added,
    modified: summary.diffByChangeType.modified,
    deleted: summary.diffByChangeType.deleted,
  };
}

export function countByFilter(rows: MethodRow[]): Record<FilterKey, number> {
  return {
    all: rows.length,
    added: rows.filter(r => r.changeType === 'added').length,
    modified: rows.filter(r => r.changeType === 'modified').length,
    deleted: rows.filter(r => r.changeType === 'deleted').length,
    critical: rows.filter(r => r.severity === 'CRITICAL').length,
    important: rows.filter(r => r.severity === 'IMPORTANT').length,
    low: rows.filter(r => r.severity === 'LOW').length,
    covered: rows.filter(
      r => r.severity === 'COVERED' || r.coveredBy.length > 0,
    ).length,
    orphans: rows.filter(r => r.severity === 'ORPHAN').length,
    leaves: rows.filter(r => r.severity === 'LEAF').length,
  };
}

const CSV_EMPTY = '\n\n        ( none )\n\n';

type MethodDevRefCsvFields = {
  exportedAt: string;
  scanId: string;
  entityId: string;
  entityKey: string;
  name: string;
  type: string;
  filePath: string;
  lineStart: string;
  lineEnd: string;
  fanInCount: string;
  fanOutCount: string;
  score: string;
  severity: string;
  testCasesCovered: string;
  testCaseCount: string;
  suggestedTests: string;
  callers_entityKeys: string;
  callees_resolved: string;
  callees_unresolved: string;
  raw_fanoutTargets: string;
};

/** Human-oriented column titles (readable in Excel and raw viewers). */
const METHOD_DEV_REF_CSV_COLUMNS: { field: keyof MethodDevRefCsvFields; header: string }[] =
  [
    { field: 'exportedAt', header: 'Exported at' },
    { field: 'scanId', header: 'Scan ID' },
    { field: 'entityId', header: 'Entity ID' },
    { field: 'entityKey', header: 'Entity key' },
    { field: 'name', header: 'Method / function name' },
    { field: 'type', header: 'Type' },
    { field: 'filePath', header: 'File path' },
    { field: 'lineStart', header: 'Line start' },
    { field: 'lineEnd', header: 'Line end' },
    { field: 'fanInCount', header: 'Fan-in count' },
    { field: 'fanOutCount', header: 'Fan-out count' },
    { field: 'score', header: 'Score' },
    { field: 'severity', header: 'Severity' },
    { field: 'testCasesCovered', header: 'Test cases (coverage)' },
    { field: 'testCaseCount', header: 'Test case count' },
    { field: 'suggestedTests', header: 'Suggested tests' },
    { field: 'callers_entityKeys', header: 'Callers (entity keys)' },
    { field: 'callees_resolved', header: 'Callees - resolved (in graph)' },
    { field: 'callees_unresolved', header: 'Callees - unresolved targets' },
    { field: 'raw_fanoutTargets', header: 'Raw fan-out targets' },
  ];

/**
* Readable label from scan entityKey-style strings (…_ts_symbol or last segment).
*/
function symbolHintFromListItem(raw: string): string {
  const key = raw.trim();
  if (!key) return '';
  const tsMatch = key.match(/_ts_(.+)$/);
  const tail = tsMatch ? tsMatch[1]! : key.split('_').pop() ?? key;
  return tail
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
* Multi-line block for quoted CSV cells: wide spacing, optional hint line.
*/
function formatCsvList(items: string[]): string {
  if (items.length === 0) {
    return CSV_EMPTY;
  }

  const rule = '    ' + '-'.repeat(44);
  const intro =
    `\n\n` +
    `${rule}\n` +
    `      Total:    ${items.length}   ${items.length === 1 ? 'entry' : 'entries'}\n` +
    `${rule}\n\n`;

  const blocks = items.map((item, index) => {
    const n = String(index + 1).padStart(2, ' ');
    const hint = symbolHintFromListItem(item);
    const showHint =
      hint.length > 0 &&
      hint.replace(/\s+/g, '').toLowerCase() !==
      item.replace(/\s+/g, '').toLowerCase();

    let block =
      `\n` +
      `        [ ${n} ]       •       ${item}\n`;

    if (showHint) {
      block +=
        `\n` +
        `                        ( readable:   ${hint} )\n`;
    }

    return block.trimEnd();
  });

  const separator = '\n\n\n\n\n';
  return intro + blocks.join(separator);
}

/**
* Single-row CSV for Dev Ref export (method metrics, tests, callers/callees).
*/
export function buildMethodDevRefCsv(row: MethodRow, scanId: string): string {
  const ent = row.entity as ScanCallableEntity & {
    entityId?: string;
    _id?: string;
    coveredBy?: string[];
  };

  const callerKeys = fanInKeys(row.entity);
  const metadata = row.entity.metadata ?? {};
  const resolved =
    metadata.resolvedFanout ?? metadata.resolvedFanouts ?? [];
  const unresolved = metadata.unresolvedFanout ?? [];
  const raw = metadata.rawFanout ?? [];

  const resolvedList = Array.isArray(resolved)
    ? resolved.map(String).filter(Boolean)
    : [];
  const unresolvedList = Array.isArray(unresolved)
    ? unresolved.map(String).filter(Boolean)
    : [];
  const rawList = Array.isArray(raw) ? raw.map(String).filter(Boolean) : [];

  const fromEntityTests = Array.isArray(ent.coveredBy)
    ? ent.coveredBy.map(String).filter(Boolean)
    : [];
  const testCases =
    row.coveredBy.length > 0 ? row.coveredBy.map(String) : fromEntityTests;

  const entityId =
    typeof ent.entityId === 'string' && ent.entityId
      ? ent.entityId
      : typeof ent._id === 'string'
        ? ent._id
        : '';

  const fields: MethodDevRefCsvFields = {
    exportedAt: new Date().toISOString(),
    scanId,
    entityId: entityId || CSV_EMPTY,
    entityKey: row.entityKey,
    name: row.name,
    type: row.type,
    filePath: row.filePath,
    lineStart: String(row.lineStart),
    lineEnd: String(row.lineEnd),
    fanInCount: String(row.fanIn),
    fanOutCount: String(row.fanOut),
    score: String(row.score),
    severity: row.severity,
    testCasesCovered: formatCsvList(testCases),
    testCaseCount: String(testCases.length),
    suggestedTests: CSV_EMPTY,
    callers_entityKeys: formatCsvList(callerKeys),
    callees_resolved: formatCsvList(resolvedList),
    callees_unresolved: formatCsvList(unresolvedList),
    raw_fanoutTargets: formatCsvList(rawList),
  };

  const headers = METHOD_DEV_REF_CSV_COLUMNS.map(c => c.header);
  const labeledRow: Record<string, string> = {};
  for (const { field, header } of METHOD_DEV_REF_CSV_COLUMNS) {
    labeledRow[header] = fields[field];
  }

  return formatCsv(headers, [labeledRow]);
}

